import { TimesheetDayStatus, OffCrewType } from '../../types';

/** One Off Crew Personnel row in the timesheet grid (Office/Visitor/Manpower) */
export interface OffCrewRow {
  key: string;
  documentId?: string;
  id?: string | number;
  type: OffCrewType;
  name: string;
  days: Record<string, TimesheetDayStatus>;
  description: string;
}

export const offCrewTypeLabels: Record<OffCrewType, string> = {
  office: 'Office',
  visitor: 'Visitor',
  manpower: 'Manpower',
};

export const offCrewNamePlaceholders: Record<OffCrewType, string> = {
  office: 'Office staff name + period on board',
  visitor: 'e.g. عدد 4 افراد ورشة ...',
  manpower: 'Manpower name / assigned works',
};

/** Compact input style for inputs living inside the timesheet tables */
export const tableInputCls =
  'w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-ocean-500/30 focus:border-ocean-500';

/** Print value per the sheet legend: work = 1, تعيين/رفت = 0.5, off = 0 */
export const dayPrintValue = (status?: TimesheetDayStatus): string =>
  status === 'work' ? '1' : status === 'half' ? '0.5' : '0';

/** "2026-07" -> number of days in that month */
export const daysInMonth = (monthValue: string): number => {
  const [y, m] = monthValue.split('-').map(Number);
  if (!y || !m) return 31;
  return new Date(y, m, 0).getDate();
};

/** "2026-07" -> "2026-07-01" (API date for the timesheet month field) */
export const monthToApiDate = (monthValue: string): string => `${monthValue}-01`;

export const currentMonthValue = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

/** Short weekday label for a day of the given month ("2026-07", 4 -> "Sa") */
export const weekdayLabel = (monthValue: string, day: number): string => {
  const [y, m] = monthValue.split('-').map(Number);
  return new Date(y, m - 1, day).toLocaleDateString('en-GB', { weekday: 'narrow' });
};

/** Click cycle: off -> work -> half (تعيين/رفت) -> off */
export const cycleStatus = (status?: TimesheetDayStatus): TimesheetDayStatus => {
  if (status === 'work') return 'half';
  if (status === 'half') return 'off';
  return 'work';
};

/** Sum of days: work = 1, half (تعيين/رفت) = 0.5 */
export const countWorkedDays = (days: Record<string, TimesheetDayStatus> | undefined): number => {
  if (!days) return 0;
  return Object.values(days).reduce((sum, s) => sum + (s === 'work' ? 1 : s === 'half' ? 0.5 : 0), 0);
};

export const statusDisplay: Record<TimesheetDayStatus, { symbol: string; cls: string; title: string }> = {
  work: { symbol: '✓', cls: 'bg-green-100 text-green-700 border-green-300', title: 'Worked (full day)' },
  half: { symbol: '½', cls: 'bg-amber-100 text-amber-700 border-amber-300', title: 'تعيين / رفت (half day)' },
  off: { symbol: '✗', cls: 'bg-gray-100 text-gray-400 border-gray-200', title: 'Not worked' },
};
